/**
 * @file     dom-tk/modal.js - Modal dialogs for the dcp-client dom-tk
 *
 * A generic modal has the following layout (dialogTemplateHtml):
 * +----------------------------------------------+
 * |                                      CLOSE X |
 * |                   TITLE                      |
 * |                                              |
 * | ICON              BODY                       |
 * |                                              |
 * |                       BUTTONS PRIMARY_BUTTON |
 * +----------------------------------------------+
 *
 * @author   Wes Garland, wes@distributive.network
 * @date     Nov 2024
 */
'use strict';

const { fifoMutex } = require('../../common/concurrency');

const dialogTemplateHtml = `
<dialog id="dcp-modal">
  <h3 id="dcp-modal-title"></h3>
  <div id="dcp-modal-close-x" role="button"></div>
  <div id="dcp-modal-content-area">
    <div id="dcp-modal-icon"></div>
    <div id="dcp-modal-body"></div>
  </div>
  <div id="dcp-modal-error" role="alert"></div>
  <div id="dcp-modal-button-area"></div>
</dialog>`;

const $ = document.querySelector.bind(document); /* jQuery: The Good Parts */
const domTemplate = new DOMParser().parseFromString(dialogTemplateHtml, 'text/html');
const modalDialogMutex = Symbol('modal-dialog-mutex');

/**
 * Close a dialog, removing it from the DOM
 *
 * @param {Object} dialogHnd     a dialogHnd object that was returned from showModalDialog
 */
exports.close = function dcpClient$$dialog$close(dialogHnd)
{
  const dialog = dialogHnd.element;
  if (!dialog)
    return false;

  dialog.close();
  dialog.remove();
}

/**
 * Show a generic modal. Modals always have the DOM id dcp-modal. This routine uses a fifo mutex
 * to ensure that we don't try to open multiple modals at once.
 *
 * A modal has the following characteristics:
 * - only one modal is visible at a time
 * - nothing happens in the app until the modal is dismissed; including the display of other modals
 * - any button causes the modal to be dismissed
 *
 * A generic modal has the following options; all are optional:
 *  - baseClass    - class name to add to the DIALOG element
 *  - class        - extra class name to add to the DIALOG element
 *  - role         - value for the ARIA role attribute
 *  - title        - title text
 *  - icon         - icon HTMLElement to display, must have be #dcp-modal-icon to preserve layout
 *  - body         - what to put in the dialog body
 *                   - string => text (newlines work)
 *                   - HTMLElement => element is appended to #dcp-modal-body element
 *  - htmlTitle    - title in HTML (ignored if title specified)
 *  - htmlBody     - body in HTML (ignored if body specified)
 *  - close        - how to handle the close X and any other non-button ways (esc) of closing the dialog
 *                   . false    => cannot close dialog without choosing an option, close-x not displayed
 *                   . number   => button number to pretend to click
 *                   . function => run that function
 *  - buttons      - array of button objects, strings, or HTMLElements. Button objects are transformed
 *                   automatically into BUTTON elements. If the first one is an object, it will be
 *                   styled as the primary button. Strings are treated as button objects with only the
 *                   text property defined. Event handlers are added for Button objects but not
 *                   HTMLElements.
 *
 *                   Button objects have the following properties:
 *                   - text: the text to put in the button
 *                   - html: the HTML to put in the button
 *                   - class: extra class name to add to the BUTTON element
 *                   - action: callback function to run when the button is clicked
 *
 *  - onShow       - callback invoked once the modal is displayed
 *  - onBeforeShow - callback invoked just before the modal is displayed
 *
 * The default way to resolve dialogHnd.promise is to click on a button. When that happens, the promise is
 * resolved with an object with the following properties:
 *  - buttonIndex: number, starting at zero: which button was clicked
 *  - button:      the DOM element corresponding to the button that was clicked
 *  - event:       the DOM event corresponding to the button click
 *
 * The user may also click on the close-x or press the escape key to resolve the promise. See 'close' above.
 *
 * @param {Object} options    object describing the appearance and basic behaviours of the dialog
 * @returns {Promise} which resolves to a dialogHnd object, which has the following properties:
 *  - element: the DOM element corresponding to the DIALOG
 *  - promise: a Promise which is resolved when the dialog is closed
 *  - resolve: a function to resolve that promise and close the dialog
 *  - reject:  a function to reject that promise and close the dialog
 *  - buttons: an array of DOM elements, displayed in #dcp-modal-button-area, made from options.buttons
 */
exports.showModalDialog = async function dcpClient$$modal$showModalDialog(options)
{
  const buttons = options.buttons?.slice() || [ 'OK' ];
  const dialog = domTemplate.querySelector('DIALOG#dcp-modal').cloneNode(true);
  const buttonArea = dialog.querySelector('#dcp-modal-button-area');

  if (!document.body || document.readyState === 'loading')
  {
    console.warn('Warning: showModalDialog invoked before the DOM was ready');
    const { promise: p$domReady, resolve } = Promise.withResolvers();
    document.addEventListener('DOMContentLoaded', resolve);
    await p$domReady;
  }

  const dialogHnd = { element: dialog, buttons: [] };
  dialogHnd.promise = new Promise((resolve, reject) => {
    dialogHnd.resolve = (...args) => { const r = resolve(...args); exports.close(dialogHnd); return r; };
    dialogHnd.reject  = (...args) => { const r = reject (...args); exports.close(dialogHnd); return r; }; // eslint-disable-line prefer-promise-reject-errors
    dialog.addEventListener('close', () => { /* intrisic DIALOG event, fired directly by browser */
      if (typeof options.close === 'function')
        'nop - should have triggered this via close-x code';
      else if (typeof options.close === 'number')
        dialogHnd.buttons[options.close].click();
      else if (!options.close)
        exports.close(dialogHnd);
      else
      {
        console.error('invalid dialogHnd close value', options.close);
        reject(new Error('invalid dialogHnd close type ' + typeof options.close));
      }
    });
  });

  dialog.setAttribute('role', options.role || 'dialog');
  if (options.baseClass)
    dialog.classList.add(options.baseClass);
  if (options.class)
    dialog.classList.add(options.class);

  if (options.title)
    dialog.querySelector('#dcp-modal-title').textContent = options.title;
  else if (options.htmlTitle)
    dialog.querySelector('#dcp-modal-title').innerHTML = options.title;
  else
    dialog.querySelector('#dcp-modal-title').classList.add('dcp-modal-hidden');

  if (options.icon === false)
    dialog.querySelector('#dcp-modal-icon').classList.add('dcp-modal-hidden');
  else if (options.icon instanceof HTMLElement)
    dialog.querySelector('#dcp-modal-icon').replaceWith(options.icon);

  const bodyElement = dialog.querySelector('#dcp-modal-body');
  if (options.htmlBody)
  {
    bodyElement.innerHTML = options.htmlBody;
    bodyElement.setAttribute('mode', 'html');
  }
  else if (options.body instanceof HTMLElement)
  {
    bodyElement.appendChild(options.body);
    bodyElement.setAttribute('mode', 'element');
  }
  else if (options.body)
  {
    bodyElement.textContent = options.body;
    bodyElement.setAttribute('mode', 'text');
  }

  for (let i=0; i < buttons.length; i++)
  {
    let button = buttons[i];
    let element;

    if (typeof button === 'string' || button instanceof String)
      button = { text: String(button) };
    if (button instanceof HTMLElement)
      element = button;
    else
    {
      element = document.createElement('BUTTON');
      if (button.text)
        element.textContent = button.text;
      else if (button.html)
        element.innerHTML = button.html;
      if (button.class)
        element.classList.add(button.class);
      if (i === 0)
        element.setAttribute('primary', 'true');

      element.addEventListener('click', function dcpClient$$modal$action(event) {
        if (button.action)
          button.action();
        dialogHnd.resolve({
          buttonIndex: i,
          button,
          event,
        });
      });
    }
    dialogHnd.buttons.push(element);
    buttonArea.appendChild(element);
  }

  if (options.close === false)
    dialog.querySelector('#dcp-modal-close-x').classList.add('dcp-modal-hidden');
  else
    dialog.querySelector('#dcp-modal-close-x').addEventListener('click', () => options.close(dialogHnd));

  dialog.id = 'dcp-modal';
  dialog.setAttribute('dcp-modal-state', 'initial');
  if (options.onBeforeShow)
    options.onBeforeShow(dialog, options);

  fifoMutex(modalDialogMutex, () => {
    document.body.appendChild(dialog);
    dialog.showModal();
    dialog.focus(); /* focus allows keyboard shortcuts to work */
    dialog.addEventListener('transitionend', () => {
      dialog.scrollIntoView({ behavior: 'smooth', block: 'center', container: 'nearest', inline: 'center' })
    });

    /* disable drag-drop except where explicitly enabled - prevent accidental navigation */
    dialog.addEventListener('drop',      (event) => { event.preventDefault() });
    dialog.addEventListener('dragover',  (event) => { event.preventDefault() });
    if (options.showCallback)
      options.showCallback(dialog);

    if (options.onShow)
      options.onBeforeShow(dialog, options);
    return dialogHnd.promise;
  });

  return dialogHnd;
}

/**
 * Implement alert dialogs. If message is an instance of Error, the stack trace becomes visible
 * after clicking on the bug icon.
 *
 * @param message        either an error message, or an instance of Error, or an instanceof ErrorEvent.
 */
exports.alert = async function dcpClient$$modal$alert(message, options)
{
  var dialogHnd;
  options = Object.assign({
    role:  'alertdialog',
    baseClass: 'dcp-modal-alert',
    close: () => dialogHnd.resolve(),
  }, options);

  if (options?.isError && !(message instanceof Error) && !(message instanceof ErrorEvent))
  {
    const err = new Error(message.message);
    Object.assign(err, message);
    message = err;
  }

  if (message instanceof ErrorEvent)
  {
    let error;

    if (message.error)
    {
      if (message.error instanceof Error)
        error = message.error;
      else if (message.error instanceof DOMException)
      {
        const de = message.error;
        error = new Error(de.message);
        switch(de.code)
        {
          case  2: message.code = 'DOMSTRING_SIZE_ERR';  break;
          case  6: message.code = 'NO_DATA_ALLOWED_ERR'; break;
          case 16: message.code = 'VALIDATION_ERR';      break;
          default: message.code = de.code;               break;
        }
        error.name = de.name;
      }
      else if (message.error instanceof DOMError)
      {
        const de = message.error;
        error = new Error(de.message || de.name);
        error.name = de.name;
      }
      else
      {
        console.warn(`unknown error type in ${exports.alert.name} ErrorEvent:`, message.error);
        error = new Error(message.error);
      }
    }
    else
    {
      error = new Error(message.message);
      error.name = message.name;
    }

    if (!message.stack && Error.captureStackTrace)
      Error.captureStackTrace(message, Error);
    if (!message.stack && typeof message.lineno === 'number')
      message.stack = `[error event] ${message.filename}:${message.lineno}:${message.colno}`;
    if (message.stack)
      error.stack = message.stack + '\n    --------------------\n' + error.stack.split('\n').slice(1).join('\n');
    message = error;
  }

  if (!(message instanceof Error))
    options[options.html ? 'htmlBody' : 'body'] = message;
  else
  {
    const error = message;
    options.body = document.createElement('DIV');
    options.body.textContent = `${message}`;

    const stackElement = document.createElement('PRE');
    stackElement.id = 'dcp-modal-stack';
    if (error.stack)
      stackElement.textContent = error.stack;
    else if (typeof error.lineNumber === 'number')
      stackElement.textContent = `${error.fileName}:${error.lineNumber}:${error.columnNumber}`;
    else
      stackElement.textContent = '[empty stack]';
    options.body.appendChild(stackElement);

    options.icon = document.createElement('DIV');
    options.icon.id = 'dcp-modal-icon';
    options.icon.addEventListener('click', () => {
      const nextToggle = stackElement.getAttribute('show-toggle') === 'on' ? 'off' : 'on';
      stackElement.setAttribute('show-toggle', nextToggle);
    });
    options.icon.setAttribute('title', error.code || '');
  }

  dialogHnd = await exports.showModalDialog(options);
  await dialogHnd.promise;
}

/**
 * Implement confirm dialog. Resolves with true or false, matching OK or Cancel
 */
exports.confirm = async function dcpClient$$modal$confirm(message, options)
{
  var dialogHnd;

  options = Object.assign({
    baseClass: 'dcp-modal-confirm',
    buttons: [ 'OK', 'Cancel' ],
    close: () => dialogHnd.resolve({ buttonIndex: -1 }),
  }, options);
  options[options.html ? 'htmlBody' : 'body'] = message;

  dialogHnd = await exports.showModalDialog(options);
  return (await dialogHnd.promise).buttonIndex === 0;
}

/**
 * Implement prompt dialog. Resolves with the text of the user's response, or null if cancelled.
 */
exports.prompt = async function dcpClient$$modal$prompt(message, defaultValue, options)
{
  var dialogHnd;

  options = Object.assign({
    baseClass: 'dcp-modal-prompt',
    buttons: [ 'OK', 'Cancel' ],
    title: window.location.hostname,
    close: () => dialogHnd.resolve({ buttonIndex: -1 }),
  }, options);

  options.body = document.createElement('DIV');
  options.body.textContent = `${message}\n`;
  const inputElement = document.createElement('INPUT');
  inputElement.defaultValue = defaultValue;
  inputElement.setAttribute('type', 'text');
  options.body.appendChild(inputElement);

  dialogHnd = await exports.showModalDialog(options);
  inputElement.focus();
  inputElement.select();
  inputElement.addEventListener('keypress', ev => {
    if (ev.code === 'Enter')
      dialogHnd.buttons[0].click();
  });

  if ((await dialogHnd.promise).buttonIndex === 0)
    return inputElement.value;
  return null;
}

/**
 * Upload a file modal dialog, into the browser. The file can be upload by INPUT type=file, drag/drop, or pasting.
 *
 * @param {string}    title     modal dialog's title
 * @param {string}    accept    file types to accept, same format as INPUT type=file
 * @param {function}  validate  a validation function; failure to validate will not dismiss the modal
 * @param {object}    options   showModalDialog() options
 */
exports.upload = async function dcpClient$$modal$upload(title, accept, validate, options)
{
  var dialogHnd;
  const body         = document.createElement('DIV');
  const inputElement = document.createElement('INPUT');
  const cancelButton = document.createElement('BUTTON');
  const browseButton = document.createElement('BUTTON');
  const buttons = [ browseButton, cancelButton, inputElement ];

  options = Object.assign({
    baseClass: 'dcp-modal-upload',
    title,
    buttons,
    body,
    close: () => dialogHnd.resolve(null)
  }, options);

  body.id = 'dcp-modal-drop-target';
  body.innerHTML = `<div id='dcp-modal-drop-label'>${options.dropLabel || 'drop file here'}</div>`;
  body.addEventListener('dragover',  (event) => event.preventDefault());
  body.addEventListener('drop',      (event) => event.preventDefault());
  body.addEventListener('dragenter', (event) => dialogHnd.element.setAttribute('drag-state', checkDataType(event.dataTransfer) ? 'over' : 'invalid'));
  body.addEventListener('dragleave', (event) => dialogHnd.element.removeAttribute('drag-state'));
  body.addEventListener('drop',      (event) => dialogHnd.element.removeAttribute('drag-state'));
  body.addEventListener('drop',      (event) => dataHandler(event.dataTransfer));

  inputElement.setAttribute('type', 'file');
  inputElement.setAttribute('accept', accept || '*');
  inputElement.setAttribute('form', body.id);
  inputElement.classList.add('dcp-modal-hidden');
  inputElement.addEventListener('change', () => uploadFile(inputElement.files[0]));

  cancelButton.textContent = 'Cancel';

  browseButton.setAttribute('primary', 'true');
  browseButton.textContent = 'Browse';
  browseButton.addEventListener('click', ev => inputElement.click());

  function checkDataType(dataTransfer)
  {
    var item;

    if (!accept)
      return true;

    for (item of dataTransfer.items)
    {
      if (item.kind === 'file')
        break;
    }
    if (!item)
      return false;

    const types = accept.split(',');
    for (let type of types)
    {
      if (item.type === type)
        return true;   /* exact mime type match */
      if (type[0] === '.')
      {
        const file = item.getAsFile();
        if (file.name.endsWith(type))
          return true; /* extension matches */
      }
      if (type.endsWith('*'))
        if (item.type.startsWith(type.slice(-1)))
          return true; /* mime-major or any match */
    }

    return false;
  }

  /**
   * Flicker the drag-state just enough to let the user know we aren't hung, so they can figure out what
   * they did wrong.
   */
  function flicker()
  {
    dialogHnd.element.setAttribute('drag-state', 'invalid');
    setTimeout(() => dialogHnd.element.setAttribute('drag-state', ''), 150);
  }

  /** Handle the paste or drop event by locating the first file in the event datatransfer*/
  function dataHandler(dataTransfer)
  {
    if (!dataTransfer?.items || !checkDataType(dataTransfer))
    {
      /* Pasted or dropped an invalid item */
      flicker();
      return;
    }

    for (let item of dataTransfer.items)
    {
      if (item.kind === 'file')
      {
        uploadFile(item.getAsFile());
        return;
      }
    }
  }

  /** "upload" the file into the browser by reading it and resolving dialog with its contents */
  function uploadFile(fileHnd)
  {
    const reader = new FileReader();
    if (!fileHnd)
      return;

    reader.onerror = (error) => dialogHnd.reject(error);
    reader.onload  = (event) => finishUpload(fileHnd.name, event.target.result);
    reader.readAsArrayBuffer(fileHnd); /* => onload */
  }

  async function finishUpload(filename, contents)
  {
    var vr = true;

    try
    {
      vr = validate ? await validate(contents) : true;
    }
    catch(error)
    {
      vr = false;
    }

    if (vr)
      dialogHnd.resolve({ filename, contents });
    else
      flicker();
  }

  dialogHnd = await exports.showModalDialog(options);
  dialogHnd.element.addEventListener('paste', (event) => dataHandler(event.clipboardData));
  cancelButton.addEventListener('click', () => dialogHnd.resolve(null));

  return dialogHnd.promise;
}

/**
 * Upload a keystore file. Adds basic input validation and error handling to generic upload modal.
 */
exports.uploadKeystore = async function dcpClient$$modal$uploadKeystore(title, options)
{
  const wallet = require('../wallet');
  var ks;

  async function validate(data)
  {
    try
    {
      const str = (new TextDecoder('utf-8')).decode(data);
      ks = await new wallet.Keystore(str);
      return true;
    }
    catch(error)
    {
      $('#dcp-modal-error').textContent = `keystore validation failure: ${error.message}`;
      return false;
    }
  }

  const res = await exports.upload(title, 'application/x-dcp-keystore,.keystore', validate, options);
  if (!res)
    return res;

  if (!ks.label)
    ks.label = res.filename;
  return ks;
}

/**
 * Prompt for a password. This is largely the same code as exports.prompt, but things are arranged so that
 * entering a password triggers the submission of an HTML form. This form submission looks like an
 * old-school FORM-based login to the browser, so it activates the password manager, indexing the
 * password against the supplied username.
 *
 * @param {string}   username     the username we are getting / setting the password for
 * @param {Object}   options      a object with all of he options for a showModalDialog dialog, plus
 *                     .prompt         - a simple context-sensitive prompt for the password; used when
 *                                       generating the dialog body internally. Default is 'Password'.
 *                     .prompt2        - a second prompt; default is 'Confirm Password'
 *                     .mode           - when 'create', render a second prompt
 *                     .body           - either a fully-formed HTMLElement for the dialog contents, or an
 *                                       Object which contains body properties to override
 *                     .inputSelectors - CSS selector string for locating input elements, defaults
 *                                       to text and password form input elements
 *                     .force          - default true, when in create mode, do not resolve the return
 *                                       promise until the passwords matches
 *                     .validate       - function used to validate that a password is correct. When this
 *                                       is specified and returns the value 'true' or a promise that
 *                                       resolves to 'true', we attempt to trigger the browser's password
 *                                       manager to save the password. The function will be invoked with
 *                                       the password as its sole argument.
 *
 * @returns {Promise} that resolves to
 *                         - null when dialog cancelled,
 *                         - false when passwords don't match,
 *                         - or the password that was entered
 */
exports.passwordPrompt = async function dcpClient$$modal$passwordPrompt(username, options)
{
  var dialogHnd, inputAttribs, inputAttribs2;
  const inputSelectors = options?.inputSelectors || ':where(INPUT[type="password"], INPUT[type="text"]):not(INPUT[name="username"])';
  const dlgOptions = Object.assign({
    prompt:    'Passphrase',
    prompt2:   'Confirm Passphrase',
    baseClass: 'dcp-modal-prompt',
    buttons:    [ 'OK', 'Cancel' ],
    title:      window.location.hostname,
    close:      () => dialogHnd.resolve({ buttonIndex: -1 }),
    force:      true,
    mismatch:   'passphrases must match',
  }, options);

  if (options?.body instanceof HTMLElement)
    dlgOptions.body = options.body;
  else
  {
    let html = `
<div id="dcp-modal-password-preamble"></div>
<form id="dcp-modal-password-form" class="dcp-modal-hidden" action="javascript:void 0;" autocomplete="off">
  <input type="text" name="username" value="${username}" autocomplete="username">
  <input type="submit">
</form>
<div>`;

    if (dlgOptions.showEye)
    {
      inputAttribs='type="text" class="dcp-show-eye" obscured';
      inputAttribs2 = inputAttribs;
    }
    else
    {
      /* Second create prompt is always a obscured text instead of password for autocomplete reasons */
      inputAttribs='type="password"'
      inputAttribs2='type="text" style="-webkit-text-security: disc;"'
    }

    if (dlgOptions.mode !== 'create')
    {
      /* DIV wrapping input preserve flexbox layout; empty span used by CSS for dcp-show-eye */
      html += `
  <div id="dcp-modal-password-prompt">
    <label for="dcp-modal-password">${dlgOptions.prompt}</label>
    <div><input ${inputAttribs} form="dcp-modal-password-form" name="dcp-modal-password" autocomplete="current-password"><span></span></div>
  </div>`;
    }
    else
    {

      html += `
  <div id="dcp-modal-password-prompt">
    <label for="dcp-modal-password">${dlgOptions.prompt}</label>
    <div><input ${inputAttribs} form="dcp-modal-password-form" name="dcp-modal-password" autocomplete="new-password"><span></span></div>
  </div>
  <div id="dcp-modal-password-confirm-prompt">
    <label for="dcp-modal-password-confirm">${dlgOptions.prompt2}</label>
    <div><input ${inputAttribs2} form="dcp-modal-password-form" name="dcp-modal-password-confirm"><span></span></div>
  </div>`;
    }

    html += '</div>';
    dlgOptions.body = document.createElement('DIV');
    dlgOptions.body.innerHTML = html;

    if (dlgOptions.preamble)
      dlgOptions.body.querySelector('#dcp-modal-password-preamble').textContent = dlgOptions.preamble;
    else if (dlgOptions.preambleHtml)
      dlgOptions.body.querySelector('#dcp-modal-password-preamble').innerHTML = dlgOptions.preambleHtml;
    if (dlgOptions.mode)
      dlgOptions.body.setAttribute('mode', dlgOptions.mode);
    if (options?.body)
      Object.assign(dlgOptions.body, options.body);
    else if (options?.bodyHtml)
      Object.assign(dlgOptions.body, options.bodyHtml);
  }

  dialogHnd = await exports.showModalDialog(dlgOptions);
  const inputElements = dialogHnd.element.querySelectorAll(inputSelectors);
  /* press enter on password field => submit */
  inputElements.forEach(inputElement => {
    inputElement.addEventListener('keypress', ev => {
      if (ev.code === 'Enter')
        dialogHnd.buttons[0].click();
    });
  });
  /* autofocus on first password field */
  if (dlgOptions.autofocus !== false)
    inputElements[0].focus();

  if (dlgOptions.showEye)
  {
    Array.from(dialogHnd.element.querySelectorAll('INPUT.dcp-show-eye + SPAN')).forEach(element => {
      element.addEventListener('click', ev => {
        const inputElement = element.previousSibling;
        if (inputElement.hasAttribute('obscured'))
          element.previousSibling.removeAttribute('obscured');
        else
          element.previousSibling.setAttribute('obscured', 'true');
      });
    });
  }

  if ((await dialogHnd.promise).buttonIndex === 0)
  {
    if (inputElements.length === 1 || inputElements[0].value === inputElements[1].value)
    {
      if (dlgOptions.validate && dlgOptions.validate(inputElements[0].value))
        window.history.pushState({}, undefined); /* trigger password manager */
      return inputElements[0].value;
    }
    return false;
  }

  return null;
}

/**
 * Handle keyboard events for modals. These don't fire when the focus ring is not on the DIALOG element
 * itself, which is what happens when the dialog is opened. So a user using keyboard navigation can
 * press either "tab enter" or just "enter" to take the primary button.
 *
 * Escape: close the current modal by clicking on the X
 * Enter:  click the default button
 */
window.addEventListener('keydown', function dcpClient$$modal$$keydownHandler(ev) {
  const dialog = document.getElementById('dcp-modal');
  if (!dialog || !dialog.hasAttribute('open') || dialog.getAttribute('open') === 'false')
    return;

  switch(ev.code)
  {
    case 'Escape':
    {
      if (event.target.id !== dialog && event.target !== document.body)
        return;
      /* Close the DIALOG via the close-x if possible. If not, let the default behaviour take over.
       * Escape key support is part of the ARIA requirements for role=dialog.  In the case where the
       * user presses Escape but there is no close-x, dialogHnd.promise will reject. (Unless the
       * dialog itself is focused, in which case nothing happens)
       */
      const closeX = dialog.querySelector('#dcp-modal-close-x');
      if (closeX)
      {
        closeX.dispatchEvent(new Event('click'));
        ev.stopPropagation();
        ev.preventDefault();
      }
      break;
    }
    /* Click the primary button when the user presses Enter. If there is no primary button, nothing
     * happens. This code is not entered when the user has already activated the focus ring.
     */
    case 'Enter':
    {
      if (event.target.id !== 'dcp-modal')
        return;

      const primaryButton = dialog.querySelector('#dcp-modal-button-area > *[primary]:not([primary="false"])');
      if (primaryButton)
        primaryButton.dispatchEvent(new Event('click'));
      ev.stopPropagation();
      ev.preventDefault();
      break;
    }
  }
});
